import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X, MapPin, Loader2, Check, Home, DollarSign, BedDouble, Bath, Ruler, Compass, Camera, Image as ImageIcon, Move } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import CnibMap from '../../components/CnibMap';
import { OPERATIONS, OPERATION_LABELS, PROPERTY_TYPES, DEPARTMENTS } from '../../lib/constants';

const EMPTY = {
  title: '', property_type: 'departamento', operation: 'venta', price_usd: '',
  city: 'Santa Cruz', zone: '', address: '', bedrooms: 2, bathrooms: 2, area_m2: 80,
  description: '', images: [], listed: true,
};

export default function MemberMap() {
  const [points, setPoints] = useState([]);
  const [operation, setOperation] = useState('');
  const [loading, setLoading] = useState(true);
  const [pinMode, setPinMode] = useState(false);
  const [pin, setPin] = useState(null); // {lat, lng}
  const [draggableMarker, setDraggableMarker] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    api
      .get('/properties/map/points')
      .then(({ data }) => setPoints(data))
      .catch(() => setPoints([]))
      .finally(() => setLoading(false));
  }, []);

  const markers = useMemo(() => {
    const items = points
      .filter((p) => !operation || p.operation === operation)
      .map((p) => {
        const op = OPERATIONS.find((o) => o.value === p.operation);
        return {
          id: p.property_id,
          lat: p.lat,
          lng: p.lng,
          color: op?.color || '#F97316',
          icon: 'home',
          popupHtml: `
            <div style="font-family:DM Sans,sans-serif;max-width:220px">
              ${p.images?.[0] ? `<img src="${p.images[0]}" style="width:100%;height:90px;object-fit:cover;border-radius:10px" />` : ''}
              <p style="font-weight:800;margin:8px 0 2px;font-size:13px;color:#0F172A">${p.title}</p>
              <p style="margin:0;font-size:11px;color:#64748B">${p.zone ? p.zone + ', ' : ''}${p.city}</p>
              <p style="margin:6px 0;font-size:14px;font-weight:800;color:#0F4C81">US$ ${Number(p.price_usd).toLocaleString('es-BO')}</p>
              <a href="/propiedades/${p.property_id}" style="display:inline-block;background:#F97316;color:white;font-weight:700;font-size:11px;padding:6px 14px;border-radius:8px;text-decoration:none">Ver ficha</a>
            </div>`,
        };
      });
    if (pin) {
      items.push({
        id: 'new-pin',
        lat: pin.lat, lng: pin.lng,
        color: '#8B5CF6',
        icon: 'dot',
        size: 'lg',
        draggable: draggableMarker,
        onDragEnd: ({ lat, lng }) => setPin({ lat, lng }),
      });
    }
    return items;
  }, [points, operation, pin, draggableMarker]);

  const handleMapClick = ({ lat, lng }) => {
    if (!pinMode) return;
    setPin({ lat, lng });
    setDraggableMarker(true);
    setPinMode(false);
  };

  const uploadImages = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        if (!file.type?.startsWith('image/')) continue;
        const fd = new FormData();
        fd.append('file', file);
        try {
          const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          if (data?.url) urls.push(data.url);
        } catch { /* skip failed */ }
      }
      setForm((f) => ({ ...f, images: [...f.images, ...urls].slice(0, 8) }));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!pin) {
      toast.error('Selecciona la ubicación en el mapa primero');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        ...form,
        price_usd: Number(form.price_usd),
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        area_m2: Number(form.area_m2),
        lat: pin.lat,
        lng: pin.lng,
      };
      const { data } = await api.post('/properties', body);
      toast.success('Propiedad publicada en el mapa');
      setPoints((prev) => [...prev, {
        property_id: data.property_id,
        title: data.title, lat: pin.lat, lng: pin.lng,
        operation: data.operation, price_usd: data.price_usd,
        city: data.city, zone: data.zone, images: data.images,
      }]);
      setModalOpen(false);
      setPin(null);
      setDraggableMarker(false);
      setForm(EMPTY);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo publicar');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelPin = () => {
    setPin(null);
    setDraggableMarker(false);
    setPinMode(false);
    setModalOpen(false);
  };

  // Field wrapper for professional labels + icon
  const Field = ({ label, hint, required, icon: Icon, children, testid, cols = 1 }) => (
    <div className={cols === 2 ? 'sm:col-span-2' : ''} data-testid={testid}>
      <label className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-600">
        {Icon && <Icon className="h-3.5 w-3.5 text-cnib-blue" />}
        {label}
        {required && <span className="text-orange-500">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
    </div>
  );

  const inputCls =
    'w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';

  return (
    <div className="space-y-4" data-testid="member-map">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Mapa colaborativo</p>
          <h1 className="font-heading text-xl font-extrabold text-slate-900 sm:text-2xl">Propiedades CNIB en vivo</h1>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="member-map-filters">
        <button
          onClick={() => setOperation('')}
          className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${!operation ? 'bg-cnib-navy text-white' : 'bg-white text-slate-500 hover:text-slate-800'}`}
        >
          Todas ({points.length})
        </button>
        {OPERATIONS.map((op) => (
          <button
            key={op.value}
            onClick={() => setOperation(op.value)}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${operation === op.value ? 'text-white' : 'bg-white text-slate-500 hover:text-slate-800'}`}
            style={operation === op.value ? { background: op.color } : {}}
          >
            {op.label}
          </button>
        ))}
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-slate-200 shadow-xl">
        {loading ? (
          <div className="flex h-[62vh] items-center justify-center bg-slate-100">
            <Loader2 className="h-10 w-10 animate-spin text-cnib-blue" />
          </div>
        ) : (
          <CnibMap
            center={[-63.6, -17.2]}
            zoom={5.5}
            markers={markers}
            onMapClick={handleMapClick}
            className={`h-[62vh] w-full ${pinMode ? 'cursor-crosshair' : ''}`}
          />
        )}

        {pinMode && (
          <div className="absolute left-1/2 top-4 z-10 -translate-x-1/2 rounded-full bg-cnib-navy px-4 py-2 text-xs font-bold text-white shadow-lg" data-testid="member-map-pin-mode-banner">
            Haz tap en el mapa para ubicar la propiedad
          </div>
        )}

        {pin && !pinMode && (
          <div className="absolute left-1/2 top-4 z-10 flex -translate-x-1/2 items-center gap-3 rounded-2xl bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-lg" data-testid="member-map-pin-adjust-banner">
            <span className="flex items-center gap-1.5">
              <Move className="h-3.5 w-3.5 text-violet-500" />
              Arrastra el pin violeta para ajustar la ubicación exacta
            </span>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="rounded-full bg-orange-500 px-3 py-1 text-[11px] font-bold text-white"
              data-testid="member-map-pin-continue"
            >
              Siguiente paso
            </button>
            <button
              type="button"
              onClick={cancelPin}
              className="rounded-full p-1 text-slate-400 hover:bg-slate-100"
              aria-label="Cancelar"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => {
            if (pin) setPinMode((v) => !v);
            else setPinMode((v) => !v);
          }}
          data-testid="member-map-add-pin"
          className={`absolute bottom-6 right-6 z-10 flex h-14 w-14 items-center justify-center rounded-full shadow-xl transition-all ${
            pinMode ? 'bg-slate-900 text-white' : 'bg-orange-500 text-white hover:scale-105 hover:bg-orange-600'
          }`}
          aria-label={pinMode ? 'Cancelar' : 'Agregar propiedad en esta ubicación'}
          title={pinMode ? 'Cancelar modo pin' : 'Agregar propiedad al mapa'}
        >
          {pinMode ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
        </button>

        <div className="absolute bottom-4 left-4 flex flex-wrap gap-2 rounded-2xl bg-white/95 px-3 py-2 text-[11px] shadow-lg backdrop-blur">
          {OPERATIONS.map((op) => (
            <span key={op.value} className="flex items-center gap-1.5 font-semibold text-slate-600">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: op.color }} />
              {OPERATION_LABELS[op.value]}
            </span>
          ))}
        </div>
      </div>

      {/* Pro modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/70 p-0 sm:items-center sm:p-6"
          onClick={() => !submitting && setModalOpen(false)}
          data-testid="member-map-add-modal"
        >
          <div
            className="w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-white sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '92vh' }}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-start justify-between border-b border-slate-100 bg-white px-6 py-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-orange-500">Nueva propiedad</p>
                <h3 className="font-heading text-xl font-extrabold text-slate-900">Publica desde el mapa</h3>
                <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                  <MapPin className="h-3.5 w-3.5 text-orange-500" />
                  {pin?.lat.toFixed(5)}, {pin?.lng.toFixed(5)}
                  <button
                    type="button"
                    onClick={() => { setModalOpen(false); }}
                    className="ml-2 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold text-slate-600 hover:bg-slate-100"
                    data-testid="member-map-edit-location"
                  >
                    <Move className="h-3 w-3" /> Ajustar en mapa
                  </button>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-full p-1 text-slate-400 hover:bg-slate-100"
                data-testid="member-map-add-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={submit} className="space-y-6 p-6">
              {/* Sección 1: Qué estás publicando */}
              <section>
                <h4 className="mb-3 flex items-center gap-2 font-heading text-sm font-extrabold text-slate-900">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-cnib-blue text-xs text-white">1</span>
                  ¿Qué estás publicando?
                </h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Título de la publicación" required icon={Home} testid="field-title" cols={2}
                         hint="Un título corto y claro, ej. 'Departamento 3 dormitorios con parqueo'.">
                    <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required minLength={4} maxLength={120} placeholder="Ej. Departamento moderno en Equipetrol" className={inputCls} data-testid="member-map-form-title" />
                  </Field>
                  <Field label="Tipo de inmueble" required testid="field-type">
                    <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className={inputCls} data-testid="member-map-form-type">
                      {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Operación" required testid="field-operation" hint="¿Está en venta, alquiler o anticrético?">
                    <select value={form.operation} onChange={(e) => setForm({ ...form, operation: e.target.value })} className={inputCls} data-testid="member-map-form-operation">
                      {OPERATIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                </div>
              </section>

              {/* Sección 2: Precio y Ubicación */}
              <section>
                <h4 className="mb-3 flex items-center gap-2 font-heading text-sm font-extrabold text-slate-900">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-cnib-blue text-xs text-white">2</span>
                  Precio y ubicación
                </h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={`Precio en US$ ${form.operation === 'alquiler' ? '(mensual)' : ''}`} required icon={DollarSign} testid="field-price">
                    <input type="number" min="0" step="1" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} required placeholder="Ej. 120000" className={inputCls} data-testid="member-map-form-price" />
                  </Field>
                  <Field label="Departamento (ciudad)" required icon={MapPin} testid="field-city">
                    <select value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required className={inputCls} data-testid="member-map-form-city">
                      {DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
                    </select>
                  </Field>
                  <Field label="Zona o barrio" icon={Compass} testid="field-zone" hint="Ej. Equipetrol Norte, Urubó, Cala Cala.">
                    <input value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} placeholder="Ej. Equipetrol Norte" className={inputCls} data-testid="member-map-form-zone" />
                  </Field>
                  <Field label="Dirección exacta" testid="field-address" hint="Opcional. Solo visible para contactos interesados.">
                    <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Calle y número" className={inputCls} data-testid="member-map-form-address" />
                  </Field>
                </div>
              </section>

              {/* Sección 3: Características */}
              <section>
                <h4 className="mb-3 flex items-center gap-2 font-heading text-sm font-extrabold text-slate-900">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-cnib-blue text-xs text-white">3</span>
                  Características del inmueble
                </h4>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Dormitorios" icon={BedDouble} testid="field-bedrooms" hint={form.property_type === 'terreno' ? 'No aplica a terrenos' : 'Cuartos / habitaciones'}>
                    <input type="number" min="0" max="30" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} className={inputCls} data-testid="member-map-form-bedrooms" />
                  </Field>
                  <Field label="Baños" icon={Bath} testid="field-bathrooms" hint="Cantidad total de baños">
                    <input type="number" min="0" max="30" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} className={inputCls} data-testid="member-map-form-bathrooms" />
                  </Field>
                  <Field label="Superficie (m²)" icon={Ruler} testid="field-area" hint="Metros cuadrados totales">
                    <input type="number" min="0" step="any" value={form.area_m2} onChange={(e) => setForm({ ...form, area_m2: e.target.value })} className={inputCls} data-testid="member-map-form-area" />
                  </Field>
                </div>
              </section>

              {/* Sección 4: Descripción */}
              <section>
                <h4 className="mb-3 flex items-center gap-2 font-heading text-sm font-extrabold text-slate-900">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-cnib-blue text-xs text-white">4</span>
                  Descripción comercial
                </h4>
                <Field label="Describe la propiedad" testid="field-description" hint="Cuenta a los interesados qué hace especial al inmueble: amenities, cercanía a servicios, acabados, etc.">
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} maxLength={4000} placeholder="Ej. Departamento en piso alto con vista panorámica, 2 estacionamientos, piscina y gimnasio…" className={`${inputCls} resize-y`} data-testid="member-map-form-description" />
                </Field>
              </section>

              {/* Sección 5: Fotos */}
              <section>
                <h4 className="mb-3 flex items-center gap-2 font-heading text-sm font-extrabold text-slate-900">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-cnib-blue text-xs text-white">5</span>
                  Galería de fotos
                </h4>
                <p className="mb-3 text-xs text-slate-500">Hasta 8 fotos. Las publicaciones con fotos reciben hasta 10 veces más contactos.</p>
                <div className="flex gap-2">
                  <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => uploadImages(Array.from(e.target.files || []))} />
                  <input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadImages(Array.from(e.target.files || []))} />
                  <button type="button" onClick={() => cameraRef.current?.click()} disabled={uploading || form.images.length >= 8} className="inline-flex items-center gap-1.5 rounded-full bg-cnib-blue px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-cnib-blue/90 disabled:opacity-60" data-testid="member-map-form-camera">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                    Tomar foto
                  </button>
                  <button type="button" onClick={() => galleryRef.current?.click()} disabled={uploading || form.images.length >= 8} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60" data-testid="member-map-form-gallery">
                    <ImageIcon className="h-3.5 w-3.5" />
                    Desde galería
                  </button>
                </div>
                {form.images.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="member-map-form-images">
                    {form.images.map((url, i) => (
                      <div key={i} className="relative aspect-square overflow-hidden rounded-xl">
                        <img src={url} alt="" className="h-full w-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }))}
                          className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-slate-900/80 text-white"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        {i === 0 && (
                          <span className="absolute bottom-1 left-1 rounded-full bg-orange-500 px-1.5 py-0.5 text-[9px] font-bold text-white">Portada</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Actions */}
              <div className="sticky bottom-0 -mx-6 flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-6 py-4">
                <button type="button" onClick={() => setModalOpen(false)} className="rounded-full px-5 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-100">
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting || !pin || !form.title || !form.price_usd}
                  data-testid="member-map-form-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-orange-600 disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Publicar en el mapa
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
